import React, { useEffect, useState } from "react";
import { FieldArray, useFormikContext } from "formik";
import AsyncFormikDropdown from "../../components/inputFields/AsyncFormikDropDown";
import InputField from "../../components/inputFields/InputField";
import { TiDeleteOutline } from "react-icons/ti";
import StyledHeadingText from "../../components/Fonts/StyledHeadingText";
import { CgAdd } from "react-icons/cg";
import MedicineScanner from "../../components/MedicineScanner";

const MedicineForm = ({ searchMedicineApi }) => {
    const { values, setFieldValue } = useFormikContext();
    const [isScannerOpen, setIsScannerOpen] = useState(false); // State to control scanner modal

    // Calculate total amount whenever medicines array is updated
    useEffect(() => {
        const totalAmount = values.medicines.reduce((total, med) => {
            const price = med.price || 0;
            const quantity = med.quantity || 0;
            return total + price * quantity;
        }, 0);
        setFieldValue("amount", totalAmount);
    }, [values.medicines, setFieldValue]);

    return (
        <div>
            <FieldArray name="medicines">
                {({ push, remove }) => (
                    <div>
                        <StyledHeadingText text={"Medicines"} />
                        <div className="flex items-center gap-2 mx-2 mt-4 justify-between">
                            <StyledHeadingText text={"Medicine*"} size={"text-sm font-semibold"} />
                            <StyledHeadingText text={"Quantity*"} size={"text-sm font-semibold"} />
                            <StyledHeadingText text={"Price*"} size={"text-sm font-semibold"} />
                            <StyledHeadingText text={"Act."} size={"text-sm font-semibold"} />
                        </div>
                        {/* Existing Medicines */}
                        {values.medicines?.map((medicine, index) => (
                            <div
                                key={index}
                                className="flex flex-row justify-between items-center gap-2 mb-4"
                            >
                                {/* Medicine Name */}
                                {medicine.medicine_name ? (
                                    <InputField
                                        name={`medicines[${index}].medicine_name`}
                                        type="text"
                                        disabled
                                    />
                                ) : (
                                    <AsyncFormikDropdown
                                        name={`medicines[${index}].medicine_name`}
                                        apiCallFunction={async (input) => {
                                            const response = await searchMedicineApi(input);
                                            return response.data.map((med) => ({
                                                value: med.medicine, // Use id as value
                                                label: med.medicine_name,
                                                price: med.amount, // Pass price for later usage
                                            }));
                                        }}
                                        needSearch
                                        onChange={(selectedOption) => {
                                            if (selectedOption) {
                                                const existingMedicineIndex = values.medicines.findIndex(
                                                    (med) => med.id === selectedOption.value
                                                );

                                                if (existingMedicineIndex !== -1) {
                                                    const updatedQuantity =
                                                        values.medicines[existingMedicineIndex].quantity + 1;
                                                    setFieldValue(
                                                        `medicines[${existingMedicineIndex}].quantity`,
                                                        updatedQuantity
                                                    );
                                                } else {
                                                    setFieldValue(`medicines[${index}]`, {
                                                        medicine_name: selectedOption.label,
                                                        id: selectedOption.value,
                                                        quantity: 1,
                                                        price: selectedOption.price,
                                                    });
                                                }
                                            }
                                        }}
                                    />
                                )}

                                {/* Quantity */}
                                <InputField
                                    name={`medicines[${index}].quantity`}
                                    type="number"
                                    placeholder="Enter Quantity"
                                />

                                {/* Price */}
                                <InputField
                                    name={`medicines[${index}].price`}
                                    placeholder="Enter Price"
                                    disable
                                />

                                {/* Delete Button */}
                                <button
                                    type="button"
                                    className="text-red-600 rounded"
                                    onClick={() => remove(index)}
                                >
                                    <TiDeleteOutline size={25} />
                                </button>
                            </div>
                        ))}

                        {/* Add New Medicine Row Button */}
                        <div className="flex flex-row gap-4 mt-4">
                            <button
                                type="button"
                                className="text-primary px-3 flex flex-row items-center gap-2 font-bold"
                                onClick={() =>
                                    push({
                                        medicine_name: "",
                                        id: "",
                                        quantity: 1,
                                        price: 0,
                                    })
                                }
                            >
                                <CgAdd /> Add Medicine
                            </button>

                            {/* Scan Medicine Button */}
                            <button
                                type="button"
                                className="text-blue-500 px-3 flex flex-row items-center gap-2 font-bold"
                                onClick={() => setIsScannerOpen(true)}
                            >
                                <CgAdd /> Scan Medicine
                            </button>
                        </div>

                        {/* Scanner Modal */}
                        <MedicineScanner
                            isOpen={isScannerOpen}
                            onClose={() => setIsScannerOpen(false)}
                            handleScanResult={(data) => {
                                const existingMedicineIndex = values.medicines.findIndex(
                                    (med) => med.id === data.medicine
                                );

                                if (existingMedicineIndex !== -1) {
                                    // Increase quantity if medicine exists
                                    const updatedQuantity =
                                        values.medicines[existingMedicineIndex].quantity + 1;
                                    setFieldValue(
                                        `medicines[${existingMedicineIndex}].quantity`,
                                        updatedQuantity
                                    );
                                } else {
                                    // Add new medicine
                                    push({
                                        medicine_name: data.medicine_name,
                                        id: data.medicine,
                                        quantity: 1,
                                        price: data.amount || 0,
                                    });
                                }
                                setIsScannerOpen(false);
                            }}
                        />
                    </div>
                )}
            </FieldArray>
        </div>
    );
};

export default MedicineForm;
